import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getFilteredCycles, getLockedCycles, Cycle, CycleFilterParams, CycleFilterResponse, addCycle, updateCycle, deleteCycle, bulkCreateCyclesFromLicenses, BulkCreateResult } from '../api/Cycles';
import { getAllClasses } from '../api/Classes';
import { getStatesForUser } from '../api/States';
import { getAllProfessions } from '../api/Professions';
import { getStateRequirementsForState, IStateRequirement } from '../api/StateRequirements';
import type { LicState, Class } from '../types';
import { getStateByID } from '../constants/statesData';
import { useAuth } from '../useAuth';
import { useTimezone } from '../hooks/useTimezone';
import CreateCustomCycleModal from '../components/CreateCustomCycleModal';
import UpgradeModal from '../components/UpgradeModal';

interface Stats {
  totalCycles: number;
  activeCycles: number;
  archivedCycles: number;
  statesManaged: number;
}

interface Pagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

interface CycleMetrics {
  completed: number;
  planned: number;
  plannedNotCompleted: number;
  toComplete: number;
  completedPercent: number;
  plannedPercent: number;
}

const CycleManagement = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { timezone, toDateInput, formatDate, getDaysUntil, getCurrentDate } = useTimezone();
  
  // Data state
  const [cycles, setCycles] = useState<Cycle[]>([]);
  const [classes, setClasses] = useState<Class[]>([]);
  const [userStates, setUserStates] = useState<LicState[]>([]);
  const [stateRequirements, setStateRequirements] = useState<Record<number, IStateRequirement[]>>({});
  const [userProfessionName, setUserProfessionName] = useState<string>('');
  const [creditTerminology, setCreditTerminology] = useState<string>('credits');
  
  // UI state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);
  const [upgradeCycleLimit, setUpgradeCycleLimit] = useState<number>(1);
  const [upgradeIsOverride, setUpgradeIsOverride] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkResult, setBulkResult] = useState<BulkCreateResult | null>(null);
  const [lockedCycles, setLockedCycles] = useState<Cycle[]>([]);
  
  // Official-requirements-available banner
  const [officialAvailableBanner, setOfficialAvailableBanner] = useState<Array<{ professionID: string; professionName: string; stateName: string }>>([]);

  // Stats state
  const [stats, setStats] = useState<Stats>({
    totalCycles: 0,
    activeCycles: 0,
    archivedCycles: 0,
    statesManaged: 0
  });
  
  // Pagination state
  const [pagination, setPagination] = useState<Pagination>({
    page: 1,
    limit: 25,
    total: 0,
    totalPages: 0
  });
  
  // Filter state
  const [search, setSearch] = useState("");
  const [filterState, setFilterState] = useState<number | undefined>();
  const [filterStatus, setFilterStatus] = useState<"active" | "archived" | "">('active');
  const [sortBy, setSortBy] = useState<"cyclename" | "cyclestart" | "cycleend" | "state">("cycleend");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  
  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [showCustomModal, setShowCustomModal] = useState(false);
  const [editingCycle, setEditingCycle] = useState<Cycle | null>(null);
  const [formData, setFormData] = useState({
    cyclename: '',
    stateID: '',
    professionID: '',
    cyclestart: '',
    cycleend: '',
    creditsRequired: 0,
    cycleLengthYears: 0,
  });

  // Load dropdown data on mount
  useEffect(() => {
    const loadDropdownData = async () => {
      if (!user) return;
      try {
        const [states, allClasses] = await Promise.all([
          getStatesForUser(user.id),
          getAllClasses({}, user.id),
        ]);
        
        setUserStates(states);
        setClasses(allClasses);
        
        // Load user profession name
        if (user.professionID) {
          try {
            const professions = await getAllProfessions();
            const profession = professions.find(p => p.professionID === user.professionID);
            setUserProfessionName(profession?.name || user.professionID);
            setCreditTerminology(profession?.creditTerminology || 'credits');
          } catch (e) {
            setUserProfessionName(user.professionID);
            setCreditTerminology('credits');
          }
        }
      } catch (err) {
        console.error("Error loading dropdown data:", err);
      }
    };
    loadDropdownData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]); // Use user?.id instead of user object to prevent infinite loop

  // Load cycles whenever filters change
  useEffect(() => {
    if (user) {
      loadCycles();
    }
  }, [user, search, filterState, filterStatus, sortBy, sortOrder, pagination.page, pagination.limit]);

  // Locked (plan-downgrade-deactivated) cycles — independent of the active/archived
  // filter above, so they stay visible as an upgrade prompt regardless of toggle state.
  useEffect(() => {
    if (!user) return;
    getLockedCycles()
      .then(setLockedCycles)
      .catch(err => console.error('Error loading locked cycles:', err));
  }, [user]);

  const loadCycles = async () => {
    if (!user) return;
    setLoading(true);
    try {
      const params: CycleFilterParams = {
        search: search || undefined,
        stateID: filterState,
        status: filterStatus || undefined,
        sortBy,
        sortOrder,
        page: pagination.page,
        limit: pagination.limit,
      };
      
      const response = await getFilteredCycles(params);
      
      setCycles(response.cycles);
      setStats(response.stats);
      setPagination(response.pagination);

      // Check if any custom cycles have official requirements now available
      const customCycles = response.cycles.filter(c => c.isCustom);
      if (customCycles.length > 0) {
        const seen = new Set<string>();
        const available: Array<{ professionID: string; professionName: string; stateName: string }> = [];
        for (const cycle of customCycles) {
          const pid = cycle.professionID;
          const sid = cycle.stateid ?? cycle.stateID;
          if (!pid || !sid) continue;
          const key = `${pid}:${sid}`;
          if (seen.has(key)) continue;
          try {
            const reqs = await getStateRequirementsForState(sid);
            if (reqs.some(r => r.professionID === pid)) {
              seen.add(key);
              available.push({
                professionID: pid,
                professionName: cycle.customProfessionName || pid,
                stateName: cycle.customStateName || cycle.state || `State ${sid}`,
              });
            }
          } catch {}
        }
        setOfficialAvailableBanner(available);
      } else {
        setOfficialAvailableBanner([]);
      }
    } catch (err) {
      console.error("Error loading cycles:", err);
      setError("Failed to load cycles");
    } finally {
      setLoading(false);
    }
  };

  // Calculate metrics for each cycle
  const getCycleMetrics = (cycle: Cycle): CycleMetrics => {
    const numericCycleId = cycle.cycleid;
    const attachedClasses = classes.filter(cls => {
      const cycleIDsArr = (cls as any).cycleIDs;
      if (Array.isArray(cycleIDsArr) && typeof numericCycleId === 'number') {
        return cycleIDsArr.includes(numericCycleId);
      }
      return false;
    });
    
    // Option 1: All attached classes count as planned, completed is a subset
    const completed    = Math.round(attachedClasses.filter(c => c.took).reduce((sum, c) => sum + (c.classLU || 0), 0) * 100) / 100;
    const totalPlanned = Math.round(attachedClasses.reduce((sum, c) => sum + (c.classLU || 0), 0) * 100) / 100;
    const plannedNotCompleted = totalPlanned - completed;
    const creditsReq = cycle.creditsRequired || 0;
    const toComplete = Math.max(0, creditsReq - totalPlanned);
    const completedPercent = creditsReq > 0 ? Math.round((completed / creditsReq) * 100) : 0;
    const plannedPercent = creditsReq > 0 ? Math.round((totalPlanned / creditsReq) * 100) : 0;
    
    return { completed, planned: totalPlanned, plannedNotCompleted, toComplete, completedPercent, plannedPercent };
  };

  const handleEdit = async (cycle: Cycle) => {
    setEditingCycle(cycle);
    
    // Map cycle's state to stateID for the dropdown
    let stateIDStr = '';
    let parsedStateID = 0;
    if (typeof cycle.stateid === 'number') {
      stateIDStr = String(cycle.stateid);
      parsedStateID = cycle.stateid;
    } else if (typeof cycle.stateID === 'number') {
      stateIDStr = String(cycle.stateID);
      parsedStateID = cycle.stateID;
    } else if (cycle.state) {
      const match = userStates.find(s => s.state === cycle.state);
      if (match && typeof match.stateID === 'number') {
        stateIDStr = String(match.stateID);
        parsedStateID = match.stateID;
      }
    }

    // Fetch state requirements to get the correct creditsRequired and cycle length
    let credits = cycle.creditsRequired || 0;
    let cycleLengthYears = 0;
    if (parsedStateID > 0) {
      try {
        let requirements = stateRequirements[parsedStateID];
        if (!requirements) {
          requirements = await getStateRequirementsForState(parsedStateID);
          setStateRequirements(prev => ({ ...prev, [parsedStateID]: requirements }));
        }
        
        // Find the professionID from cycle or user's license
        let professionID = cycle.professionID || user?.professionID || '';
        if (user?.licenses && user.licenses.length > 0) {
          const matchingLicense = user.licenses.find(lic => lic.stateID === parsedStateID);
          if (matchingLicense && matchingLicense.professionID) {
            professionID = matchingLicense.professionID;
          }
        }
        
        const requirement = requirements?.find(r => r.professionID === professionID);
        if (requirement?.totalCreditsRequired) {
          credits = requirement.totalCreditsRequired;
        }
        if (requirement?.cycleLengthYears) {
          cycleLengthYears = requirement.cycleLengthYears;
        }
      } catch (e) {
        console.error('Failed to fetch state requirements for edit', e);
      }
    }

    // Fallback: derive cycle length from existing dates if requirement lookup missed it
    if (cycleLengthYears === 0 && cycle.cyclestart && cycle.cycleend) {
      const ms = new Date(cycle.cycleend).getTime() - new Date(cycle.cyclestart).getTime();
      cycleLengthYears = Math.round(ms / (365.25 * 24 * 60 * 60 * 1000));
    }

    setFormData({
      cyclename: cycle.cyclename,
      stateID: stateIDStr,
      professionID: cycle.professionID || user?.professionID || '',
      cyclestart: cycle.cyclestart ? toDateInput(cycle.cyclestart) : '',
      cycleend: cycle.cycleend ? toDateInput(cycle.cycleend) : '',
      creditsRequired: credits,
      cycleLengthYears,
    });
    
    setShowModal(true);
    setError('');
    setSuccessMessage('');
  };

  const handleDelete = async (cycleId: string) => {
    if (window.confirm('Are you sure you want to delete this cycle?')) {
      try {
        await deleteCycle(cycleId);
        setSuccessMessage('Cycle deleted successfully!');
        await loadCycles();
        setTimeout(() => setSuccessMessage(''), 3000);
      } catch (err: any) {
        const errorMsg = err?.response?.data?.message || err?.response?.data?.error || 'Failed to delete cycle.';
        setError(errorMsg);
      }
    }
  };

  const handleDuplicate = (cycle: Cycle) => {
    setEditingCycle(null);
    
    // Map cycle's state
    let stateIDStr = '';
    if (typeof cycle.stateid === 'number') {
      stateIDStr = String(cycle.stateid);
    } else if (typeof cycle.stateID === 'number') {
      stateIDStr = String(cycle.stateID);
    }

    // Calculate next cycle period (add cycle length years)
    const currentEnd = new Date(cycle.cycleend);
    const currentStart = new Date(cycle.cyclestart);
    const cycleLengthMs = currentEnd.getTime() - currentStart.getTime();
    const cycleLengthDays = Math.round(cycleLengthMs / (1000 * 60 * 60 * 24));
    const cycleLengthYears = Math.round(cycleLengthDays / 365);
    
    const nextStart = new Date(currentEnd);
    nextStart.setDate(nextStart.getDate() + 1);
    
    const nextEnd = new Date(nextStart);
    nextEnd.setFullYear(nextEnd.getFullYear() + cycleLengthYears);
    nextEnd.setDate(nextEnd.getDate() - 1);

    setFormData({
      cyclename: `${cycle.cyclename} (Copy)`,
      stateID: stateIDStr,
      professionID: cycle.professionID || user?.professionID || '',
      cyclestart: nextStart.toISOString().split('T')[0],
      cycleend: nextEnd.toISOString().split('T')[0],
      creditsRequired: cycle.creditsRequired || 0,
      cycleLengthYears,
    });
    
    setShowModal(true);
    setError('');
    setSuccessMessage('');
  };

  const handleInputChange = async (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;

    if (name === 'stateID') {
      console.log('🔍 [CycleManagement] State selection changed:', value);
      const parsedStateID = parseInt(value, 10);
      const selectedState = !isNaN(parsedStateID)
        ? userStates.find(s => s.stateID === parsedStateID)
        : undefined;
      const currentYear = new Date().getFullYear();
      
      // Find the professionID from the user's licenses that matches this state
      let userProfession = user?.professionID || '';
      if (user?.licenses && user.licenses.length > 0) {
        const matchingLicense = user.licenses.find(lic => lic.stateID === parsedStateID);
        if (matchingLicense && matchingLicense.professionID) {
          userProfession = matchingLicense.professionID;
          console.log('🎯 Found matching license with profession:', userProfession, 'for state:', parsedStateID);
        } else {
          console.log('⚠️ No matching license found for state:', parsedStateID, 'using default:', userProfession);
        }
      }
      console.log('👤 User profession for cycle:', userProfession);
      
      // Fetch requirements if not already loaded
      let requirements = stateRequirements[parsedStateID];
      console.log('📋 Cached requirements for stateID', parsedStateID, ':', requirements);
      if (!requirements && !isNaN(parsedStateID)) {
        console.log('🌐 Fetching requirements from API...');
        try {
          const reqs = await getStateRequirementsForState(parsedStateID);
          console.log('✅ Fetched requirements:', reqs);
          setStateRequirements(prev => ({ ...prev, [parsedStateID]: reqs }));
          requirements = reqs;
        } catch (e) {
          console.error('❌ Failed to load state requirements', e);
        }
      }
      
      // Calculate credits and years outside setFormData so we can log them
      const requirement = requirements?.find(r => r.professionID === userProfession);
      console.log('🎯 Matched requirement for profession', userProfession, ':', requirement);
      const years = requirement?.cycleLengthYears || 0;
      const credits = requirement?.totalCreditsRequired || 0;
      console.log('💰 Setting credits to:', credits, 'years to:', years);
      
      setFormData(prev => {
        let nextStart = prev.cyclestart;
        let nextEnd = prev.cycleend;
        
        // Handle fixed cycle dates
        if (requirement?.hasFixedCycleDates) {
          const now = new Date();
          const currentYear = now.getFullYear();
          
          if (requirement.fixedCycleStartMonth && requirement.fixedCycleStartDay) {
            const startDate = new Date(currentYear, requirement.fixedCycleStartMonth - 1, requirement.fixedCycleStartDay);
            nextStart = startDate.toISOString().split('T')[0];
            
            if (requirement.fixedCycleEndMonth && requirement.fixedCycleEndDay) {
              const endYear = currentYear + Math.floor(years);
              const endDate = new Date(endYear, requirement.fixedCycleEndMonth - 1, requirement.fixedCycleEndDay);
              nextEnd = endDate.toISOString().split('T')[0];
            }
          }
        } else if (prev.cyclestart && years > 0) {
          const d = new Date(prev.cyclestart);
          if (!isNaN(d.getTime())) {
            d.setFullYear(d.getFullYear() + years);
            nextEnd = d.toISOString().split('T')[0];
          }
        }
        
        const updatedData = {
          ...prev,
          stateID: value,
          professionID: userProfession,  // Set the profession from the license
          creditsRequired: credits,
          cycleLengthYears: years,
          cyclestart: nextStart,
          cycleend: nextEnd,
          cyclename: (!editingCycle && selectedState && userProfession)
            ? `${userProfession}-${selectedState.state}-${currentYear}`
            : prev.cyclename,
        };
        console.log('✨ Form data updated:', updatedData);
        return updatedData;
      });
      return;
    }

    // Auto-calc end date when start date changes
    if (name === 'cyclestart') {
      console.log('📅 Start date changed:', value);
      const years = formData.cycleLengthYears;
      console.log('📅 Cycle length years:', years);
      let nextEnd = '';
      if (value && years > 0) {
        const parts = value.split('-');
        const d = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
        console.log('📅 Parsed start date:', d);
        if (!isNaN(d.getTime())) {
          d.setFullYear(d.getFullYear() + years);
          d.setDate(d.getDate() - 1); // Subtract 1 day (e.g., 10/1/2024 + 3 years = 10/1/2027 - 1 day = 9/30/2027)
          nextEnd = d.toISOString().split('T')[0];
          console.log('📅 Calculated end date:', nextEnd);
        }
      } else {
        console.log('📅 Cannot calculate end date - value:', value, 'years:', years);
      }
      setFormData(prev => ({
        ...prev,
        cyclestart: value,
        cycleend: nextEnd || prev.cycleend,
      }));
      return;
    }

    // Auto-calc start date when end date changes
    if (name === 'cycleend') {
      console.log('📅 End date changed:', value);
      const years = formData.cycleLengthYears;
      console.log('📅 Cycle length years:', years);
      let nextStart = '';
      if (value && years > 0) {
        const parts = value.split('-');
        const d = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
        console.log('📅 Parsed end date:', d);
        if (!isNaN(d.getTime())) {
          d.setFullYear(d.getFullYear() - years);
          d.setDate(d.getDate() + 1); // Add 1 day (e.g., 9/30/2027 - 3 years = 9/30/2024 + 1 day = 10/1/2024)
          nextStart = d.toISOString().split('T')[0];
          console.log('📅 Calculated start date:', nextStart);
        }
      } else {
        console.log('📅 Cannot calculate start date - value:', value, 'years:', years);
      }
      setFormData(prev => ({
        ...prev,
        cycleend: value,
        cyclestart: nextStart || prev.cyclestart,
      }));
      return;
    }

    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? Number(value) : value,
    }));
  };

  const resetForm = () => {
    setEditingCycle(null);
    setFormData({ cyclename: '', stateID: '', professionID: '', cyclestart: '', cycleend: '', creditsRequired: 0, cycleLengthYears: 0 });
    setShowModal(false);
    setError('');
    setSuccessMessage('');
  };

  const handleFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');

    try {
      const parsedStateID = parseInt(formData.stateID, 10);
      if (isNaN(parsedStateID)) {
        setError('Please select a state for the cycle.');
        return;
      }

      const selectedState = userStates.find(s => s.stateID === parsedStateID);
      if (!selectedState) {
        setError('Selected state not found.');
        return;
      }

      if (!formData.cyclename || !formData.cyclestart || !formData.cycleend) {
        setError('Please fill in all required fields.');
        return;
      }

      const payload: any = {
        cyclename: formData.cyclename.trim(),
        state: selectedState.state,
        stateid: parsedStateID,
        professionID: formData.professionID || user?.professionID || '',
        cyclestart: formData.cyclestart,
        cycleend: formData.cycleend,
        creditsRequired: formData.creditsRequired,
      };

      if (user?.id) {
        payload.userId = user.id;
      }

      if (editingCycle) {
        const updatePayload: any = { ...payload };
        if (editingCycle.cycleid !== undefined) {
          updatePayload.cycleid = editingCycle.cycleid;
        }
        await updateCycle(editingCycle._id, updatePayload);
        setSuccessMessage('Cycle updated successfully!');
      } else {
        await addCycle(payload);
        setSuccessMessage('Cycle added successfully!');
      }
      
      resetForm();
      await loadCycles();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err: any) {
      if (err.response?.data?.code === 'FREE_CYCLE_LIMIT') {
        resetForm();
        setUpgradeCycleLimit(err.response.data.maxCycles ?? 1);
        setUpgradeIsOverride(err.response.data.isOverride ?? false);
        setShowUpgradePrompt(true);
        setShowUpgradeModal(true);
        return;
      }
      const serverMsg = err.response?.data?.message || err.response?.data?.error;
      setError(serverMsg || 'Failed to save cycle.');
    }
  };

  const handleBulkCreate = async () => {
    if (!window.confirm('Create a renewal cycle for every license that doesn\'t already have one? You can review and delete any you don\'t want afterward.')) return;
    setBulkLoading(true);
    setError('');
    try {
      const result = await bulkCreateCyclesFromLicenses();
      setBulkResult(result);
      await loadCycles();
    } catch (err: any) {
      if (err.response?.data?.code === 'FREE_CYCLE_LIMIT' || err.response?.data?.code === 'SOLO_STATE_LIMIT') {
        setUpgradeCycleLimit(err.response.data.maxCycles ?? 1);
        setUpgradeIsOverride(err.response.data.isOverride ?? false);
        setShowUpgradeModal(true);
        return;
      }
      setError(err.response?.data?.error || 'Failed to bulk-create cycles.');
    } finally {
      setBulkLoading(false);
    }
  };

  const handleSort = (field: "cyclename" | "cyclestart" | "cycleend" | "state") => {
    if (sortBy === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortOrder("asc");
    }
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handlePageChange = (newPage: number) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handleLimitChange = (newLimit: number) => {
    setPagination({ ...pagination, limit: newLimit, page: 1 });
  };

  const clearFilters = () => {
    setSearch("");
    setFilterState(undefined);
    setFilterStatus("active");
    setSortBy("cycleend");
    setSortOrder("desc");
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const exportToCSV = () => {
    const headers = ["Cycle Name", "State", "Start Date", "End Date", "Required", "Completed", "Planned", "To Plan", "Progress %"];
    const rows = cycles.map(c => {
      const metrics = getCycleMetrics(c);
      return [
        c.cyclename,
        c.state || '',
        formatDate(c.cyclestart),
        formatDate(c.cycleend),
        c.creditsRequired.toString(),
        metrics.completed.toString(),
        metrics.planned.toString(),
        metrics.toComplete.toString(),
        `${metrics.plannedPercent}%`
      ];
    });
    
    const csvContent = [
      headers.join(","),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(","))
    ].join("\n");
    
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `cycles_${getCurrentDate()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const getStatusBadge = (cycle: Cycle) => {
    const metrics = getCycleMetrics(cycle);
    const daysRemaining = getDaysUntil(cycle.cycleend);
    
    if (daysRemaining < 0) {
      return <span className="badge bg-secondary">Archived</span>;
    }
    
    if (metrics.plannedPercent >= 100) {
      return <span className="badge bg-success">Complete</span>;
    }
    
    if (daysRemaining <= 30 && metrics.plannedPercent < 100) {
      return <span className="badge bg-danger">At Risk</span>;
    }
    
    if (metrics.plannedPercent >= 50) {
      return <span className="badge bg-warning text-dark">In Progress</span>;
    }
    
    return <span className="badge bg-info text-white">Started</span>;
  };

  if (loading && !cycles.length) {
    return (
      <div className="container mt-4">
        <div className="text-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mt-4">
      {/* Header */}
      <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center mb-4 gap-3">
        <h2 className="mb-0">Compliance Cycles</h2>
        <div className="d-flex flex-column flex-sm-row gap-2 w-100 w-sm-auto">
          <button onClick={exportToCSV} className="btn btn-outline-secondary btn-sm">
            ▤ Export CSV
          </button>
          <button onClick={() => { resetForm(); setShowModal(true); }} className="btn btn-primary btn-sm">
            + Add New Cycle
          </button>
          <button onClick={() => setShowCustomModal(true)} className="btn btn-outline-primary btn-sm">
            <i className="bi bi-plus-circle me-2"></i>
            Create Custom Cycle
          </button>
          <button onClick={handleBulkCreate} className="btn btn-outline-success btn-sm" disabled={bulkLoading}>
            {bulkLoading ? (
              <><span className="spinner-border spinner-border-sm me-2" />Creating…</>
            ) : (
              <><i className="bi bi-collection me-2"></i>Create Cycles for All Licenses</>
            )}
          </button>
        </div>
      </div>

      {/* Official requirements available banner */}
      {officialAvailableBanner.length > 0 && (
        <div className="d-flex align-items-start gap-3 mb-4 p-3 rounded" style={{ background: '#fffbeb', border: '1px solid #f59e0b' }}>
          <div style={{ fontSize: '1.4rem', lineHeight: 1, flexShrink: 0 }}>⚡</div>
          <div>
            <div className="fw-semibold mb-1" style={{ color: '#92400e' }}>Official CE requirements are now available</div>
            <div className="text-muted small mb-2">
              CEC Track has official renewal requirements for the following profession×state combinations
              where you have custom cycles. We recommend switching to official cycles so compliance
              tracking uses the correct categories and credit minimums.
            </div>
            <ul className="mb-2 ps-3 small" style={{ color: '#78350f' }}>
              {officialAvailableBanner.map(item => (
                <li key={`${item.professionID}:${item.stateName}`}>
                  <strong>{item.professionName} ({item.professionID})</strong> — {item.stateName}
                </li>
              ))}
            </ul>
            <div className="small" style={{ color: '#92400e' }}>
              Delete the custom cycle → click <strong>+ Add New Cycle</strong> → select the profession and state to create an official cycle with pre-filled dates and categories.
              Your existing classes are <strong>not deleted</strong> — reassign them to the new cycle afterward.
            </div>
          </div>
        </div>
      )}

      {/* Helper callout */}
      <div className="d-flex align-items-center gap-3 mb-4 p-3 rounded" style={{ background: '#eef6fb', border: '1px solid #c2dff0' }}>
        <div style={{ fontSize: '1.8rem', lineHeight: 1 }}>📂</div>
        <div>
          <div className="fw-semibold mb-1">Cycles are containers for your CE classes</div>
          <div className="text-muted small">
            Each cycle holds the classes you took during that renewal period. Click <strong>View Classes</strong> on any cycle (or click its name) to open the detail page where you add, edit, and track individual courses.
          </div>
        </div>
      </div>

      {showUpgradePrompt && (
        <div style={{
          background: 'linear-gradient(135deg, #0F1F3D 0%, #1a3560 100%)',
          borderRadius: 12,
          padding: '1.75rem 2rem',
          color: '#fff',
          marginBottom: '1.25rem',
          boxShadow: '0 4px 20px rgba(15,31,61,0.25)',
        }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem', flex: 1 }}>
              <span style={{ fontSize: '2rem', lineHeight: 1, flexShrink: 0 }}>
                {upgradeIsOverride ? '🏅' : '🔒'}
              </span>
              <div>
                <div style={{ fontWeight: 700, fontSize: '1.05rem', marginBottom: 6 }}>
                  {upgradeIsOverride
                    ? 'Founding Member Benefit — Upgrade to Continue'
                    : 'Upgrade to Pro to Add More Cycles'}
                </div>
                <div style={{ opacity: 0.88, fontSize: '0.875rem', lineHeight: 1.6, marginBottom: '1rem' }}>
                  {upgradeIsOverride ? (
                    <>
                      As a founding member, you've been tracking up to{' '}
                      <strong>{upgradeCycleLimit} compliance cycle{upgradeCycleLimit !== 1 ? 's' : ''}</strong> —
                      our thank-you for being with CEC Track from the very beginning.
                      To add a {upgradeCycleLimit + 1}{(['st','nd','rd'])[upgradeCycleLimit] ?? 'th'} cycle or beyond,{' '}
                      <strong>Pro</strong> is the natural next step.
                    </>
                  ) : (
                    <>
                      You've reached the free plan limit. Upgrade to <strong>Pro</strong> to track unlimited
                      cycles across multiple states, professions, and license periods.
                    </>
                  )}
                </div>
                <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap', fontSize: '0.82rem', opacity: 0.8, marginBottom: '1.25rem' }}>
                  {['Unlimited cycles', 'Multi-state & profession tracking', 'PDF compliance reports', 'Certificate storage'].map(f => (
                    <span key={f} style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                      <span style={{ color: '#14b8a6', fontWeight: 700 }}>✓</span> {f}
                    </span>
                  ))}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                  <a
                    href="/pricing"
                    style={{
                      background: '#14b8a6',
                      color: '#fff',
                      padding: '0.55rem 1.4rem',
                      borderRadius: 8,
                      fontWeight: 700,
                      fontSize: '0.9rem',
                      textDecoration: 'none',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    Upgrade to Pro →
                  </a>
                  <span style={{ opacity: 0.55, fontSize: '0.8rem' }}>Plans start at $3/mo</span>
                </div>
              </div>
            </div>
            <button
              onClick={() => setShowUpgradePrompt(false)}
              style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.5)', fontSize: '1.4rem', cursor: 'pointer', lineHeight: 1, flexShrink: 0, padding: 0 }}
              aria-label="Dismiss"
            >
              ×
            </button>
          </div>
        </div>
      )}

      {error && <div className="alert alert-danger alert-dismissible fade show" role="alert">
        {error}
        <button type="button" className="btn-close" onClick={() => setError('')}></button>
      </div>}

      {successMessage && <div className="alert alert-success alert-dismissible fade show" role="alert">
        {successMessage}
        <button type="button" className="btn-close" onClick={() => setSuccessMessage('')}></button>
      </div>}

      {/* Stats Cards */}
      <div className="row g-3 mb-4">
        {[
          { label: 'Total Cycles',    value: stats.totalCycles,    colorClass: 'cec-card-navy'  },
          { label: 'Active',          value: stats.activeCycles,   colorClass: 'cec-card-green' },
          { label: 'Archived',        value: stats.archivedCycles, colorClass: 'cec-card-amber' },
          { label: 'States Managed',  value: stats.statesManaged,  colorClass: 'cec-card-cyan'  },
        ].map(({ label, value, colorClass }) => (
          <div key={label} className="col-6 col-md-3">
            <div className={`card cec-stat-card ${colorClass}`}>
              <div className="card-body p-3">
                <div className="cec-stat-label">{label}</div>
                <div className="cec-stat-value">{value}</div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Search and Filters */}
      <div className="card mb-4">
        <div className="card-body">
          <div className="row g-3">
            <div className="col-12 col-md-5">
              <label htmlFor="search" className="form-label small">Search by Name</label>
              <input
                id="search"
                type="text"
                className="form-control form-control-sm"
                placeholder="Search cycles..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPagination(prev => ({ ...prev, page: 1 }));
                }}
              />
            </div>
            <div className="col-6 col-md-4">
              <label htmlFor="state-filter" className="form-label small">State</label>
              <select
                id="state-filter"
                className="form-select form-select-sm"
                value={filterState || ''}
                onChange={(e) => {
                  setFilterState(e.target.value ? parseInt(e.target.value) : undefined);
                  setPagination(prev => ({ ...prev, page: 1 }));
                }}
              >
                <option value="">All States</option>
                {userStates.map((s: LicState) => (
                  <option key={s.stateID} value={s.stateID}>{s.state}</option>
                ))}
              </select>
            </div>
            <div className="col-6 col-md-2">
              <label htmlFor="sort-by" className="form-label small">Sort By</label>
              <select
                id="sort-by"
                className="form-select form-select-sm"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as any)}
              >
                <option value="cycleend">End Date</option>
                <option value="cyclestart">Start Date</option>
                <option value="cyclename">Name</option>
                <option value="state">State</option>
              </select>
            </div>
            <div className="col-6 col-md-1 d-flex align-items-end">
              <button onClick={clearFilters} className="btn btn-outline-secondary btn-sm w-100">
                Clear
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Results Header */}
      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
        <div className="text-muted small">
          Showing {pagination.total === 0 ? 0 : ((pagination.page - 1) * pagination.limit + 1)} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} cycle{pagination.total !== 1 ? 's' : ''}
        </div>
        <div className="d-flex align-items-center gap-3">
          {/* Archived toggle */}
          <button
            onClick={() => {
              setFilterStatus(filterStatus === 'active' ? '' : 'active');
              setPagination(prev => ({ ...prev, page: 1 }));
            }}
            style={{
              display: 'flex', alignItems: 'center', gap: 8,
              background: 'none', border: 'none', cursor: 'pointer', padding: 0,
            }}
            title={filterStatus === 'active' ? 'Show archived cycles' : 'Hide archived cycles'}
          >
            <div style={{
              width: 36, height: 20, borderRadius: 10, flexShrink: 0,
              background: filterStatus !== 'active' ? '#0F1F3D' : '#d1d5db',
              position: 'relative', transition: 'background 0.2s',
            }}>
              <div style={{
                width: 14, height: 14, borderRadius: '50%', background: '#fff',
                position: 'absolute', top: 3,
                left: filterStatus !== 'active' ? 19 : 3,
                transition: 'left 0.18s',
                boxShadow: '0 1px 3px rgba(0,0,0,0.25)',
              }} />
            </div>
            <span style={{
              fontSize: '0.8rem', fontWeight: 500,
              color: filterStatus !== 'active' ? '#374151' : '#9ca3af',
              whiteSpace: 'nowrap',
            }}>
              Show archived
              {stats.archivedCycles > 0 && (
                <span style={{
                  marginLeft: 5, fontSize: '0.7rem', fontWeight: 600,
                  background: filterStatus !== 'active' ? '#e2e8f0' : '#f1f5f9',
                  color: '#64748b', padding: '1px 6px', borderRadius: 10,
                }}>
                  {stats.archivedCycles}
                </span>
              )}
            </span>
          </button>

          <div className="d-flex align-items-center gap-2">
            <label htmlFor="limit-select" className="small mb-0">Rows:</label>
            <select
              id="limit-select"
              className="form-select form-select-sm"
              style={{ width: 'auto' }}
              value={pagination.limit}
              onChange={(e) => handleLimitChange(parseInt(e.target.value))}
            >
              <option value="10">10</option>
              <option value="25">25</option>
              <option value="50">50</option>
            </select>
          </div>
        </div>
      </div>

      {/* Cycles List */}
      {cycles.length === 0 ? (
        <div className="card">
          <div className="card-body text-center text-muted py-5">
            {loading ? "Loading..." : (
              filterStatus === 'active' && stats.archivedCycles > 0 ? (
                <>
                  No active cycles right now — you have {stats.archivedCycles} archived cycle{stats.archivedCycles !== 1 ? 's' : ''}.{' '}
                  <button
                    className="btn btn-link p-0 align-baseline"
                    onClick={() => { setFilterStatus(''); setPagination(prev => ({ ...prev, page: 1 })); }}
                  >
                    Show archived cycles
                  </button>
                </>
              ) : lockedCycles.length > 0 ? (
                <>No cycles found in this view — see your {lockedCycles.length} locked cycle{lockedCycles.length !== 1 ? 's' : ''} below.</>
              ) : (
                "No cycles found"
              )
            )}
          </div>
        </div>
      ) : (
        <div className="row g-3 mb-4">
          {cycles.map(cycle => {
            const metrics = getCycleMetrics(cycle);
            const daysRemaining = getDaysUntil(cycle.cycleend);
            
            return (
              <div key={cycle._id} className="col-12">
                <div className="card">
                  <div className="card-body">
                    <div className="d-flex justify-content-between align-items-start mb-3">
                      <div className="flex-grow-1">
                        <div className="d-flex align-items-center gap-2 mb-1 flex-wrap">
                          <h5 className="mb-0">
                            <Link to={`/cycles/${cycle._id}`} className="cycle-name-link">
                              {cycle.cyclename}
                            </Link>
                          </h5>
                          {getStatusBadge(cycle)}
                          {(() => {
                            const count = classes.filter(cls => {
                              const ids = (cls as any).cycleIDs;
                              return Array.isArray(ids) && typeof cycle.cycleid === 'number' && ids.includes(cycle.cycleid);
                            }).length;
                            return (
                              <span className="badge bg-light text-secondary border" style={{ fontSize: '0.7rem', fontWeight: 500 }}>
                                {count} {count === 1 ? 'class' : 'classes'}
                              </span>
                            );
                          })()}
                        </div>
                        <div className="text-muted small">
                          {cycle.state || 'Unknown State'} • {formatDate(cycle.cyclestart)} - {formatDate(cycle.cycleend)}
                          {daysRemaining >= 0 && <span className="ms-2">({daysRemaining} days remaining)</span>}
                        </div>
                      </div>
                      <div className="d-flex gap-2 flex-shrink-0">
                        <Link to={`/cycles/${cycle._id}`} className="btn btn-sm btn-primary">
                          View Classes →
                        </Link>
                        <button className="btn btn-sm btn-outline-secondary" onClick={() => handleDuplicate(cycle)} title="Duplicate cycle">
                          📋
                        </button>
                        <button className="btn btn-sm btn-outline-secondary" onClick={() => handleEdit(cycle)}>
                          Edit
                        </button>
                        <button className="btn btn-sm btn-outline-danger" onClick={() => handleDelete(cycle._id)}>
                          Delete
                        </button>
                      </div>
                    </div>
                    
                    {/* Progress Bar */}
                    <div className="mb-2">
                      <div className="d-flex justify-content-between align-items-center mb-1">
                        <small className="text-muted">Progress</small>
                        <small className="fw-bold">{metrics.plannedPercent}%</small>
                      </div>
                      <div className="progress" style={{ height: '8px' }}>
                        {/* Completed portion (green) */}
                        <div 
                          className="bg-success progress-bar"
                          role="progressbar" 
                          style={{ width: `${Math.min(100, metrics.completedPercent)}%` }}
                          title={`Completed: ${metrics.completed} ${creditTerminology}`}
                        ></div>
                        {/* Planned but not completed (yellow striped) */}
                        <div 
                          className="bg-warning progress-bar progress-bar-striped"
                          role="progressbar" 
                          style={{ width: `${Math.min(100 - metrics.completedPercent, metrics.plannedPercent - metrics.completedPercent)}%` }}
                          title={`Planned (not completed): ${metrics.plannedNotCompleted} ${creditTerminology}`}
                        ></div>
                      </div>
                    </div>
                    
                    {/* Metrics */}
                    <div className="row small text-center">
                      <div className="col-3">
                        <div className="text-muted">Required</div>
                        <div className="fw-bold">{cycle.creditsRequired} {creditTerminology}</div>
                      </div>
                      <div className="col-3">
                        <div className="text-muted">Completed</div>
                        <div className="fw-bold text-success">{metrics.completed} {creditTerminology}</div>
                      </div>
                      <div className="col-3">
                        <div className="text-muted">Planned</div>
                        <div className="fw-bold text-info">{metrics.planned} {creditTerminology}</div>
                      </div>
                      <div className="col-3">
                        <div className="text-muted">To Plan</div>
                        <div className={`fw-bold ${metrics.toComplete > 0 ? 'text-warning' : 'text-success'}`}>
                          {metrics.toComplete} {creditTerminology}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Pagination Controls */}
      {pagination.totalPages > 1 && (
        <nav aria-label="Cycles pagination">
          <ul className="pagination justify-content-center">
            <li className={`page-item ${pagination.page === 1 ? 'disabled' : ''}`}>
              <button
                className="page-link"
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={pagination.page === 1}
              >
                Previous
              </button>
            </li>

            {(() => {
              const pages = [];
              const maxVisible = 5;
              let startPage = Math.max(1, pagination.page - Math.floor(maxVisible / 2));
              let endPage = Math.min(pagination.totalPages, startPage + maxVisible - 1);

              if (endPage - startPage < maxVisible - 1) {
                startPage = Math.max(1, endPage - maxVisible + 1);
              }

              if (startPage > 1) {
                pages.push(
                  <li key="page-1" className="page-item">
                    <button className="page-link" onClick={() => handlePageChange(1)}>1</button>
                  </li>
                );
                if (startPage > 2) {
                  pages.push(<li key="ellipsis-1" className="page-item disabled"><span className="page-link">...</span></li>);
                }
              }

              for (let i = startPage; i <= endPage; i++) {
                pages.push(
                  <li key={`page-${i}`} className={`page-item ${pagination.page === i ? 'active' : ''}`}>
                    <button className="page-link" onClick={() => handlePageChange(i)}>{i}</button>
                  </li>
                );
              }

              if (endPage < pagination.totalPages) {
                if (endPage < pagination.totalPages - 1) {
                  pages.push(<li key="ellipsis-2" className="page-item disabled"><span className="page-link">...</span></li>);
                }
                pages.push(
                  <li key={`page-${pagination.totalPages}`} className="page-item">
                    <button className="page-link" onClick={() => handlePageChange(pagination.totalPages)}>{pagination.totalPages}</button>
                  </li>
                );
              }

              return pages;
            })()}

            <li className={`page-item ${pagination.page === pagination.totalPages ? 'disabled' : ''}`}>
              <button
                className="page-link"
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={pagination.page === pagination.totalPages}
              >
                Next
              </button>
            </li>
          </ul>
        </nav>
      )}

      {/* Locked cycles — set up before a plan downgrade, preserved but not usable until upgrade */}
      {lockedCycles.length > 0 && (
        <div className="card mb-4" style={{ borderColor: '#d1d5db' }}>
          <div className="card-body">
            <div className="d-flex align-items-start gap-3 mb-3">
              <div style={{ fontSize: '1.6rem', lineHeight: 1, flexShrink: 0 }}>🔒</div>
              <div className="flex-grow-1">
                <div className="fw-semibold mb-1">
                  {lockedCycles.length} Locked Cycle{lockedCycles.length !== 1 ? 's' : ''}
                </div>
                <div className="text-muted small mb-2">
                  These cycles were set up before your plan changed. Nothing was deleted — upgrade to
                  restore full access to all of them, including your classes and progress.
                </div>
                <a
                  href="/pricing"
                  className="btn btn-sm"
                  style={{ background: '#0F1F3D', color: '#fff', fontWeight: 600 }}
                >
                  Upgrade to Restore {lockedCycles.length === 1 ? 'This Cycle' : 'All Cycles'} →
                </a>
              </div>
            </div>

            <div className="row g-2">
              {lockedCycles.map(cycle => (
                <div key={cycle._id} className="col-12 col-md-6 col-lg-4">
                  <div
                    className="border rounded p-3 h-100"
                    style={{ background: '#f9fafb', opacity: 0.85 }}
                  >
                    <div className="d-flex justify-content-between align-items-start gap-2">
                      <div className="fw-semibold small">{cycle.cyclename}</div>
                      <span className="badge bg-secondary flex-shrink-0">Locked</span>
                    </div>
                    <div className="text-muted small mt-1">
                      {cycle.state || getStateByID(cycle.stateid ?? cycle.stateID ?? -1)?.name || ''}
                      {cycle.professionID ? ` · ${cycle.professionID}` : ''}
                    </div>
                    <div className="text-muted small">
                      {formatDate(cycle.cyclestart)} – {formatDate(cycle.cycleend)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Modal for Add/Edit */}
      {showModal && (
        <div className="modal show d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{editingCycle ? 'Edit Cycle' : 'Add New Cycle'}</h5>
                <button type="button" className="btn-close" onClick={resetForm}></button>
              </div>
              <form onSubmit={handleFormSubmit}>
                <div className="modal-body">
                  {userProfessionName && (
                    <div className="alert alert-secondary mb-3">
                      <strong>Profession:</strong> {userProfessionName}
                    </div>
                  )}

                  <div className="mb-3">
                    <label htmlFor="stateID" className="form-label">State *</label>
                    <select
                      id="stateID"
                      name="stateID"
                      className="form-select"
                      value={formData.stateID}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="">Select a state</option>
                      {userStates.map((s) => (
                        <option key={s._id} value={String(s.stateID)}>{s.state}</option>
                      ))}
                    </select>
                  </div>

                  {formData.stateID && formData.cycleLengthYears > 0 && (
                    <div className="alert alert-info">
                      <strong>State Requirement:</strong> {formData.creditsRequired} credits required every {formData.cycleLengthYears} year{formData.cycleLengthYears !== 1 ? 's' : ''}
                    </div>
                  )}

                  <div className="mb-3">
                    <label htmlFor="cyclename" className="form-label">Cycle Name *</label>
                    <input
                      type="text"
                      id="cyclename"
                      name="cyclename"
                      className="form-control"
                      value={formData.cyclename}
                      onChange={handleInputChange}
                      required
                    />
                  </div>

                  <div className="mb-3">
                    <label htmlFor="cyclestart" className="form-label">Start Date *</label>
                    <input
                      type="date"
                      id="cyclestart"
                      name="cyclestart"
                      className="form-control"
                      value={formData.cyclestart}
                      onChange={handleInputChange}
                      required
                    />
                  </div>

                  <div className="mb-3">
                    <label htmlFor="cycleend" className="form-label">End Date *</label>
                    <input
                      type="date"
                      id="cycleend"
                      name="cycleend"
                      className="form-control"
                      value={formData.cycleend}
                      onChange={handleInputChange}
                      required
                    />
                  </div>

                  <div className="mb-3">
                    <label htmlFor="creditsRequired" className="form-label">Credits Required</label>
                    <input
                      type="number"
                      id="creditsRequired"
                      name="creditsRequired"
                      className="form-control"
                      value={formData.creditsRequired}
                      readOnly
                      title="Auto-populated from state's CEC requirement"
                    />
                    <small className="text-muted">Auto-filled from state's CEC requirement</small>
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={resetForm}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary">
                    {editingCycle ? 'Update Cycle' : 'Add Cycle'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Custom Cycle Modal */}
      <CreateCustomCycleModal
        show={showCustomModal}
        onHide={() => setShowCustomModal(false)}
        onSuccess={() => {
          setSuccessMessage('Custom cycle created successfully! Our team has been notified.');
          loadCycles();
        }}
      />

      {showUpgradeModal && (
        <UpgradeModal
          type="pro"
          onClose={() => setShowUpgradeModal(false)}
        />
      )}

      {/* Bulk-create result modal */}
      {bulkResult && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Cycles Created</h5>
                <button type="button" className="btn-close" onClick={() => setBulkResult(null)} />
              </div>
              <div className="modal-body">
                {bulkResult.created.length > 0 && (
                  <>
                    <p className="fw-semibold text-success mb-2">
                      ✓ Created {bulkResult.created.length} cycle{bulkResult.created.length !== 1 ? 's' : ''}
                    </p>
                    <ul className="mb-3">
                      {bulkResult.created.map(c => (
                        <li key={c._id}>{c.cyclename}</li>
                      ))}
                    </ul>
                  </>
                )}
                {bulkResult.created.length === 0 && (
                  <p className="text-muted">No new cycles were created — every license already has one, or none could be auto-created.</p>
                )}
                {bulkResult.skipped.length > 0 && (
                  <>
                    <p className="fw-semibold text-warning mb-2">
                      ⚠ Skipped {bulkResult.skipped.length} license{bulkResult.skipped.length !== 1 ? 's' : ''}
                    </p>
                    <div className="table-responsive">
                      <table className="table table-sm">
                        <thead>
                          <tr><th>License</th><th>State</th><th>Reason</th></tr>
                        </thead>
                        <tbody>
                          {bulkResult.skipped.map((s, i) => (
                            <tr key={i}>
                              <td className="text-nowrap">{s.professionID} · {s.licenseNumber}</td>
                              <td className="text-nowrap">{getStateByID(s.stateID)?.name || `State ${s.stateID}`}</td>
                              <td className="small text-muted">{s.reason}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setBulkResult(null)}>Close</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CycleManagement;
